import type { ImportHalfEdge, RoofHalfEdge, RoofVertex } from "building-types/roofVolumes/roofVolumeTypes";
import { ensureDefined } from "common/utils/assertOfType";
import type { PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { planeBSPNew } from "geom/planeBSP";
import type { Vec3 } from "geom/vec3";
import { vec3Cross, vec3Dist, vec3Dot, vec3Len, vec3Normalize, vec3Scale, vec3Sub } from "geom/vec3";
import { connectHalfEdges } from "./vertexUtils";

// A bidirectional half-edge can be extended forwards or backwards.
export function isBidirectional(halfEdge: RoofHalfEdge) {
  const leftHalf = halfEdge.twin?.next?.twin;
  const rightHalf = halfEdge.prev?.twin;
  const leftPlane = leftHalf && getPlane(leftHalf);
  const rightPlane = rightHalf && getPlane(rightHalf);
  if (!rightPlane || !leftPlane) {
    return false;
  }

  // For the left and right neighbors check if their faces point towards or away from the incoming ray.
  // If they are in opposite directions, the half-edge is bidirectional.
  const [dirX, dirY, dirZ] = getDirection(halfEdge);
  const { nX: lX, nY: lY, nZ: lZ } = leftPlane;
  const { nX: rX, nY: rY, nZ: rZ } = rightPlane;

  const rDot = vec3Dot(rX, rY, rZ, dirX, dirY, dirZ);
  const lDot = vec3Dot(lX, lY, lZ, dirX, dirY, dirZ);
  if (Math.abs(rDot) < DIST_EPSILON && Math.abs(lDot) < DIST_EPSILON) {
    return true;
  }

  const leftSign = lDot > 0 === isReflex(leftHalf);
  const rightSign = rDot > 0 === isReflex(rightHalf);
  return leftSign !== rightSign;
}

// Returns direction of the half-edge.  It is assumed that
// the caller will destructure the return value to extract the contents.
export function getDirection(halfEdge: RoofHalfEdge): Vec3 {
  const p0 = halfEdge.origin?.position;
  const p1 = halfEdge.twin?.origin?.position;
  if (p0 && p1) {
    // Subtract the two positions if they exist.
    let [dirX, dirY, dirZ] = vec3Sub(p1[0], p1[1], p1[2], p0[0], p0[1], p0[2]);
    [dirX, dirY, dirZ] = vec3Normalize(dirX, dirY, dirZ);
    return [dirX, dirY, dirZ];
  } else if (p0) {
    // Otherwise use the direction vectors.
    return halfEdge.twin?.origin?.dir || [NaN, NaN, NaN];
  } else if (p1 && halfEdge.origin?.dir) {
    const dir = ensureDefined(ensureDefined(halfEdge.origin).dir);
    return vec3Scale(dir[0], dir[1], dir[2], -1);
  } else {
    return [NaN, NaN, NaN];
  }
}

// Returns position of the half-edge.
export function getPosition(halfEdge: RoofHalfEdge) {
  return halfEdge.origin?.position || halfEdge.twin?.origin?.position || [NaN, NaN, NaN];
}

// Get the most previous half-edge we can find.
export function getMostPrevious(halfEdge: RoofHalfEdge) {
  let prev = halfEdge;
  while (prev.prev) {
    prev = prev.prev;
  }
  return prev;
}

// Get the most next half-edge we can find.
export function getMostNext(halfEdge: RoofHalfEdge) {
  let next = halfEdge;
  while (next.next) {
    next = next.next;
  }
  return next;
}

// Find the plane for a given half-edge.
// A half-edge and its previous half-edge define a plane unless
// the edges are colinear.
export function getPlaneBase(halfEdge: RoofHalfEdge): PlaneBSP | null {
  if (!halfEdge.prev) {
    return null;
  }
  const [dirX, dirY, dirZ] = getDirection(halfEdge);
  const [prevX, prevY, prevZ] = getDirection(halfEdge.prev);
  if (isNaN(prevX)) {
    return null;
  }
  let [nx, ny, nz] = vec3Cross(dirX, dirY, dirZ, prevX, prevY, prevZ);

  // Return null if the directions are parallel.
  if (vec3Len(nx, ny, nz) < DIST_EPSILON) {
    return null;
  }
  [nx, ny, nz] = vec3Normalize(nx, ny, nz);
  // Flip the normal if it is convex.
  if (!halfEdge.convex) {
    [nx, ny, nz] = vec3Scale(nx, ny, nz, -1);
  }
  const [posX, posY, posZ] = getPosition(halfEdge);
  const d = vec3Dot(nx, ny, nz, posX, posY, posZ);
  if (isNaN(nx)) {
    return null;
  }
  return planeBSPNew(nx, ny, nz, d);
}

// Find the plane for a given half-edge.
export function getPlane(halfEdge: RoofHalfEdge): PlaneBSP | null {
  let plane = getPlaneBase(halfEdge);
  let next = halfEdge.next;
  let prev = halfEdge.prev;
  // Keep trying the previous or next half-edge if getPlaneBase returns null.
  while (!plane && next) {
    plane = getPlaneBase(next);
    next = next.next;
  }
  if (plane) {
    return plane;
  }
  while (!plane && prev) {
    plane = getPlaneBase(prev);
    prev = prev.prev;
  }
  return plane;
}

// Return true if this is a reflex angle.
export function isReflex(halfEdge: RoofHalfEdge): boolean {
  const rightPlane = getPlane(halfEdge);
  const leftPlane = halfEdge.twin && getPlane(halfEdge.twin);
  if (!leftPlane || !rightPlane) {
    return false;
  }
  const [dirX, dirY, dirZ] = getDirection(halfEdge);
  const { nX: lx, nY: ly, nZ: lz } = leftPlane;
  const { nX: rx, nY: ry, nZ: rz } = rightPlane;
  const [crossX, crossY, crossZ] = vec3Cross(lx, ly, lz, rx, ry, rz);
  return vec3Dot(crossX, crossY, crossZ, dirX, dirY, dirZ) < 0;
}

// Update the convexity to match the normal.
export function updateConvex(halfEdge: RoofHalfEdge, nx: number, ny: number, nz: number) {
  const plane = getPlane(halfEdge);
  if (!plane) {
    return;
  }
  const { nX: hx, nY: hy, nZ: hz } = plane;
  if (vec3Dot(hx, hy, hz, nx, ny, nz) < 0) {
    halfEdge.convex = !halfEdge.convex;
  }
}

// Find the length of a half-edge.
export function getEdgeLength(halfA: RoofHalfEdge) {
  const [ax, ay, az] = getPosition(halfA);
  if (!halfA.twin) {
    return NaN;
  }
  const [bx, by, bz] = getPosition(halfA.twin);
  return vec3Dist(ax, ay, az, bx, by, bz);
}

// Merge one half-edge with its twins. Merge their two vertices into one. Removes the half-edge
// and the twin and keeps the other half-edges.
export function mergeWithTwin(halfA: RoofHalfEdge) {
  const halfB = halfA.twin;
  const vertexA = halfA.origin;
  const vertexB = halfB?.origin;
  if (!halfB || !vertexA || !vertexB) {
    return;
  }
  const halfEdgesA = vertexA.halfEdges;
  const halfEdgesB = vertexB.halfEdges;
  const indexA = halfEdgesA.indexOf(halfA);
  const indexB = halfEdgesB.indexOf(halfB);
  // Remove halfB. Reorder half-edges with the next one first.
  const bHalfs = halfEdgesB.slice(indexB + 1).concat(halfEdgesB.slice(0, indexB));
  halfEdgesA.splice(indexA, 1, ...bHalfs);
  bHalfs.forEach((half) => {
    half.origin = vertexA;
  });
  connectHalfEdges(vertexA);
}

// Export a half-edge into a format that can be imported.
export function exportHalfEdge(half: RoofHalfEdge, halfEdges: RoofHalfEdge[], vertices: RoofVertex[]): ImportHalfEdge {
  return {
    origin: half.origin ? vertices.indexOf(half.origin) : -1,
    twin: half.twin ? halfEdges.indexOf(half.twin) : -1,
    next: half.next ? halfEdges.indexOf(half.next) : -1,
    prev: half.prev ? halfEdges.indexOf(half.prev) : -1,
    convex: half.convex,
  };
}
